// CRM B-IV: die Anhänge einer erfassten Mail.
//
// Das Dashboard speichert sie nicht — es holt sie beim Klick aus dem Postfach. Der
// eigentliche Punkt des Pakets: **auch ein Kollege**, der die Mail über den
// Firmenzugriff sieht, bekommt sie, obwohl sie im Postfach eines anderen liegt. Die
// Berechtigung prüft das Lambda, nicht diese Komponente.
//
// Erst auf Klick geladen: die Liste kostet einen Graph-Aufruf, und ein Verlauf mit
// dreißig Mails würde sonst dreißig davon auslösen, bevor jemand einen davon will.
import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { callLambda } from "../helpers/api/gateway";
import DocumentPreviewModal, {
  canPreviewDocument,
} from "./DocumentPreviewModal";
import HiddenValue from "./HiddenValue";

// autoLoad: hoechstens 3 Abrufe gleichzeitig — Graph erlaubt ~4 parallele Anfragen
// pro Postfach und antwortet darueber mit 429.
const AUTO_LOAD_LIMIT = 3;
let autoLoadsRunning = 0;
const autoLoadQueue = [];
const withAutoLoadSlot = async (fn) => {
  if (autoLoadsRunning < AUTO_LOAD_LIMIT) autoLoadsRunning++;
  else await new Promise((resolve) => autoLoadQueue.push(resolve));
  try {
    return await fn();
  } finally {
    const next = autoLoadQueue.shift();
    if (next) next(); // der Platz geht direkt weiter
    else autoLoadsRunning--;
  }
};

// Die Anhaenge einer erfassten Mail aendern sich nicht: einmal geholt, gilt die Liste
// fuer die Sitzung — Tabwechsel und Zurueckblaettern kosten keinen Graph-Aufruf mehr.
const attachmentCache = new Map();

const formatBytes = (bytes) =>
  bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

/**
 * @param emailId          die Mail, deren Anhaenge gezeigt werden
 * @param onPreviewChange  optional: meldet, ob gerade eine Vorschau offen ist. Steht
 *                         die Liste IM Lesefenster (EmailFullBody), blendet sich jenes
 *                         damit aus, statt sich unter die Vorschau zu stapeln.
 * @param autoLoad         sofort laden statt auf Klick — nur wo die Anhaenge der Grund
 *                         sind, die Zeile zu sehen (Dokumente-Tab, eine Seite = hoechstens
 *                         25 Aufrufe, 3 gleichzeitig), nicht im Verlauf.
 */
const EmailAttachments = ({ emailId, onPreviewChange, autoLoad = false }) => {
  const { t } = useTranslation(["components", "common"]);
  const [files, setFiles] = useState(() => attachmentCache.get(emailId) ?? null);
  const [busy, setBusy] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  // quiet: beim automatischen Laden kein Toast pro Zeile — die Zeile faellt auf den
  // Button zurueck, und erst ein Klick darauf meldet den Fehler.
  const load = async (quiet = false) => {
    setBusy(true);
    try {
      const result = await callLambda("lineupdashboardApiCrmMail", {
        body: { action: "attachments", emailId },
      });
      const list = result?.attachments || [];
      attachmentCache.set(emailId, list);
      setFiles(list);
    } catch (error) {
      console.error("Anhangliste fehlgeschlagen", error);
      if (quiet !== true) toast.error(t("Could not load the attachments."));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!autoLoad || attachmentCache.has(emailId)) return undefined;
    // Beim Blaettern nicht mehr gestartete Abrufe verfallen, statt die Warteschlange
    // der neuen Seite zu blockieren — und geben den Button wieder frei.
    let cancelled = false;
    setBusy(true);
    withAutoLoadSlot(() => (cancelled ? setBusy(false) : load(true)));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailId, autoLoad]);

  // Die Datei kommt NICHT durch das Gateway, sondern über eine vorsignierte URL —
  // sonst scheiterte jeder Anhang über 10 MB. Dieselbe URL bedient Download und
  // Vorschau; das `Content-Disposition: attachment` der URL macht das Modal mit
  // seinem typisierten Blob wieder unschädlich.
  const attachmentUrl = async (file) => {
    const result = await callLambda("lineupdashboardApiCrmMail", {
      body: { action: "attachment", emailId, attachmentId: file.id, name: file.name },
    });
    if (!result?.url) throw new Error("Keine URL für den Anhang erhalten.");
    return result.url;
  };

  const download = async (file) => {
    setBusy(true);
    try {
      window.open(await attachmentUrl(file), "_blank", "noopener");
    } catch (error) {
      console.error("Anhang-Download fehlgeschlagen", error);
      toast.error(t("Could not load the attachment."));
    } finally {
      setBusy(false);
    }
  };

  // Der Anhang trägt `contentType` aus Graph, nicht `fileType` wie eine Documents-Zeile.
  const asDocument = (file) =>
    file && { id: file.id, name: file.name, fileType: file.contentType };

  // Auf- und Zumachen laufen ueber diese zwei, damit die Meldung nach oben nicht
  // vergessen werden kann.
  const showPreview = (file) => {
    setPreviewFile(file);
    if (onPreviewChange) onPreviewChange(true);
  };
  const hidePreview = () => {
    setPreviewFile(null);
    if (onPreviewChange) onPreviewChange(false);
  };

  if (files === null) {
    return (
      <Button
        variant="link"
        size="sm"
        className="p-0 small text-muted"
        disabled={busy}
        onClick={load}
      >
        <i className="mdi mdi-paperclip me-1" />
        {busy ? t("Loading...") : t("Show attachments")}
      </Button>
    );
  }

  if (!files.length) {
    return <span className="small text-muted">{t("No attachments.")}</span>;
  }

  return (
    <div className="d-flex flex-wrap gap-2 small align-items-center">
      {files.map((file) => (
        <span key={file.id} className="d-inline-flex align-items-center">
          <Button
            variant="outline-secondary"
            size="sm"
            className="py-0 px-2"
            disabled={busy}
            onClick={() =>
              canPreviewDocument(asDocument(file))
                ? showPreview(file)
                : download(file)
            }
          >
            <i className="mdi mdi-paperclip me-1" />
            <HiddenValue dataClass="documents">{file.name}</HiddenValue>
            {file.size ? <span className="text-muted ms-1">{formatBytes(file.size)}</span> : null}
          </Button>
          {canPreviewDocument(asDocument(file)) && (
            <Button
              variant="link"
              size="sm"
              className="py-0 px-1 text-muted"
              title={t("Show Preview")}
              onClick={() => showPreview(file)}
            >
              <i className="mdi mdi-file-eye-outline" />
            </Button>
          )}
          <Button
            variant="link"
            size="sm"
            className="py-0 px-1 text-muted"
            title={t("Download")}
            disabled={busy}
            onClick={() => download(file)}
          >
            <i className="mdi mdi-download" />
          </Button>
        </span>
      ))}
      <DocumentPreviewModal
        document={asDocument(previewFile)}
        onHide={hidePreview}
        getUrl={() => attachmentUrl(previewFile)}
      />
    </div>
  );
};

export default EmailAttachments;
